import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ChevronLeft, Check, Clock, CalendarClock, Sparkles, ShieldCheck } from "lucide-react";

const dstr = (d) => d.toISOString().slice(0, 10);

export default function PortalBook() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [service, setService] = useState(null);
  const [optionals, setOptionals] = useState([]);
  const [date, setDate] = useState(dstr(new Date()));
  const [operator, setOperator] = useState("any");
  const [avail, setAvail] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [privacyRead, setPrivacyRead] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [wlPreferredTime, setWlPreferredTime] = useState("");
  const [wlAnyTime, setWlAnyTime] = useState(false);
  const [wlBusy, setWlBusy] = useState(false);

  useEffect(() => {
    api.get("/portal/services").then((r) => setServices(r.data));
    api.get("/booking-config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const byId = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);
  const optionalDeps = useMemo(() => {
    if (!service) return [];
    return (service.dependencies || []).filter((d) => d.type === "optional").map((d) => byId[d.service_id]).filter(Boolean);
  }, [service, byId]);
  const includedDeps = useMemo(() => {
    if (!service) return [];
    return (service.dependencies || [])
      .filter((d) => ["mandatory", "included", "supplement"].includes(d.type))
      .map((d) => ({ svc: byId[d.service_id], qty: d.quantity || 1, type: d.type }))
      .filter((x) => x.svc);
  }, [service, byId]);

  const cats = useMemo(() => {
    const g = {};
    services.forEach((s) => { (g[s.category] = g[s.category] || []).push(s); });
    return g;
  }, [services]);

  const loadAvail = useCallback(async () => {
    if (!service) return;
    setLoading(true); setSlot(null);
    try {
      const oids = optionals.join(",");
      const op = operator !== "any" ? operator : "";
      const { data } = await api.get(`/portal/availability?date=${date}&service_ids=${service.id}&optional_ids=${oids}&operator_id=${op}`);
      setAvail(data);
    } catch (e) { toast.error("Errore disponibilità"); } finally { setLoading(false); }
  }, [service, optionals, operator, date]);

  useEffect(() => { if (step === 2 && service) loadAvail(); }, [step, service, loadAvail]);

  const confirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/portal/book", { service_ids: [service.id], optional_ids: optionals, start: slot.start, operator_id: slot.operators[0].id });
      setDone(data);
      setStep(4);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore prenotazione"); } finally { setLoading(false); }
  };

  const next7 = Array.from({ length: 14 }, (_, i) => new Date(Date.now() + i * 86400000));

  const payDeposit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/payments/simulate/${done.payment.id}`, { outcome: "succeeded" });
      setDone({ ...done, payment: { ...done.payment, status: data.status } });
      toast.success("Caparra pagata (test)");
    } catch (e) { toast.error("Errore pagamento"); } finally { setLoading(false); }
  };

  if (done) return (
    <div className="text-center py-10">
      <div className="mx-auto h-16 w-16 rounded-full bg-[hsl(var(--hi-status-success)/0.15)] flex items-center justify-center"><Check className="h-8 w-8 text-[hsl(var(--hi-status-success))]" /></div>
      <h1 className="font-heading text-3xl font-semibold mt-4">Prenotazione confermata</h1>
      <p className="text-muted-foreground mt-1">Codice: <span className="font-mono font-semibold">{done.confirmation_code}</span></p>
      <Card className="p-4 mt-5 text-left">
        <div className="font-medium">{(done.appointment.service_names || []).join(", ")}</div>
        <div className="text-sm text-muted-foreground">{new Date(done.appointment.start).toLocaleString("it-IT", { timeZone: "Europe/Rome", weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}</div>
        <div className="text-sm mt-1">{eur(done.appointment.price)}</div>
      </Card>
      <p className="text-xs text-muted-foreground mt-3">Riceverai le comunicazioni configurate ({done.communications} eventi pianificati).</p>
      {done.payment && done.payment.status !== "paid" && (
        <Card className="p-4 mt-4 text-left border-[hsl(var(--hi-gold)/0.4)]">
          <div className="flex items-center justify-between"><span className="font-medium">Caparra richiesta</span><span className="font-heading text-xl font-semibold">{eur(done.payment.amount)}</span></div>
          <p className="text-xs text-muted-foreground mt-1">Pagamento in modalità Test/Sandbox — nessun addebito reale.</p>
          <Button className="w-full mt-3" onClick={payDeposit} disabled={loading} data-testid="book-pay-deposit">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Paga caparra (test)</Button>
        </Card>
      )}
      {done.payment && done.payment.status === "paid" && (
        <div className="mt-4 flex items-center justify-center gap-2 text-[hsl(var(--hi-status-success))] text-sm"><Check className="h-4 w-4" /> Caparra pagata (test)</div>
      )}
      <div className="flex gap-2 mt-5"><Button className="flex-1" onClick={() => nav("/portale/appuntamenti")}>I miei appuntamenti</Button><Button variant="outline" className="flex-1" onClick={() => nav("/portale")}>Home</Button></div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {step > 1 && step < 4 && <Button variant="ghost" size="icon" onClick={() => setStep(step - 1)} data-testid="book-back"><ChevronLeft className="h-5 w-5" /></Button>}
        <h1 className="font-heading text-2xl font-semibold">Prenota</h1>
        <Badge variant="secondary" className="ml-auto">Passo {step}/3</Badge>
      </div>

      {step === 1 && (
        <div className="space-y-5">
          {Object.entries(cats).map(([cat, list]) => (
            <div key={cat}>
              <h3 className="font-heading text-lg font-semibold mb-2">{cat}</h3>
              <div className="space-y-2">
                {list.map((s) => (
                  <button key={s.id} onClick={() => { setService(s); setOptionals([]); setStep(optionalDepsOf(s, byId).length ? 1.5 : 2); }} data-testid={`book-service-${s.id}`}
                    className={`w-full text-left rounded-xl border p-3 flex justify-between items-center ${service?.id === s.id ? "border-[hsl(var(--hi-gold))]" : "border-border"} hover:bg-accent`}>
                    <div><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.duration_min}' {(s.dependencies||[]).length ? "· servizio completo" : ""}</div></div>
                    <div className="font-heading font-semibold">{s.price_from ? "da " : ""}{eur(s.price)}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {step === 1.5 && service && (
        <div className="space-y-4">
          <Card className="p-4">
            <div className="font-medium">{service.name}</div>
            {includedDeps.length > 0 ? (
              <div className="mt-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Il servizio include</div>
                <ul className="text-sm space-y-0.5">
                  {includedDeps.map(({ svc, qty, type }) => (
                    <li key={svc.id} className="flex justify-between" data-testid={`book-included-${svc.id}`}>
                      <span>{qty > 1 ? `${qty}× ` : ""}{svc.name}</span>
                      <span className="text-muted-foreground">{type === "included" ? "incluso" : `+${eur(svc.price * qty)}`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground mt-1">Vuoi aggiungere un extra?</div>
            )}
          </Card>
          {optionalDeps.length > 0 && <div className="text-xs uppercase tracking-wider text-muted-foreground">Extra consigliati (facoltativi)</div>}
          {optionalDeps.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><div className="font-medium">{o.name}</div><div className="text-xs text-muted-foreground">+{eur(o.price)} · {o.duration_min}'</div></div>
              <Switch checked={optionals.includes(o.id)} onCheckedChange={(v) => setOptionals((p) => v ? [...p, o.id] : p.filter((x) => x !== o.id))} data-testid={`book-opt-${o.id}`} />
            </div>
          ))}
          <Button className="w-full" onClick={() => setStep(2)} data-testid="book-optionals-next">Continua</Button>
        </div>
      )}

      {step === 2 && service && (
        <div className="space-y-4">
          <Card className="p-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--hi-gold))]" /><span className="text-sm">{service.name}{optionals.length ? ` + ${optionals.length} extra` : ""}</span></Card>
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Giorno</div>
            <div className="flex gap-2 overflow-x-auto hi-scroll pb-1">
              {next7.map((d) => { const k = dstr(d); return (
                <button key={k} onClick={() => setDate(k)} data-testid={`book-day-${k}`} className={`shrink-0 rounded-xl border px-3 py-2 text-center ${date === k ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold-soft))]" : "border-border"}`}>
                  <div className="text-[10px] uppercase">{d.toLocaleDateString("it-IT", { weekday: "short" })}</div>
                  <div className="font-semibold">{d.getDate()}</div>
                </button>); })}
            </div>
          </div>
          {cfg?.allow_choose_professional && (
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Professionista</div>
              <Select value={operator} onValueChange={setOperator}><SelectTrigger data-testid="book-operator"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="any">Primo disponibile</SelectItem>{[...new Map((avail?.slots||[]).flatMap((s) => s.operators).map((o) => [o.id, o])).values()].map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
            </div>
          )}
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Orari disponibili</div>
            {loading ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--hi-gold))]" /></div> : (
              (avail?.slots || []).length === 0 ? (
                <Card className="p-4 text-center text-sm" data-testid="book-no-slots">
                  <p className="text-foreground font-medium">Il professionista non è al momento disponibile.</p>
                  {cfg?.waitlist_enabled && <div className="mt-3"><Button variant="outline" size="sm" onClick={() => { setWlPreferredTime(""); setWlAnyTime(false); setWaitlistOpen(true); }} data-testid="book-waitlist"><CalendarClock className="h-4 w-4 mr-1" /> Entra in lista d'attesa</Button></div>}
                </Card>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {avail.slots.map((s) => <button key={s.time} onClick={() => setSlot(s)} data-testid={`book-slot-${s.time}`} className={`rounded-lg border py-2 text-sm font-medium ${slot?.time === s.time ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold-soft))]" : "border-border"}`}>{s.time}</button>)}
                </div>
              )
            )}
          </div>
          {avail && <Card className="p-3 flex justify-between"><span className="text-sm text-muted-foreground"><Clock className="h-4 w-4 inline mr-1" />{avail.duration_min}'</span><span className="font-semibold">{avail.price_from ? "da " : ""}{eur(avail.price)}</span></Card>}
          <Button className="w-full" disabled={!slot} onClick={() => setStep(3)} data-testid="book-to-summary">Continua</Button>
        </div>
      )}

      {step === 3 && service && slot && (
        <div className="space-y-4">
          <Card className="p-5 space-y-2">
            <h3 className="font-heading text-xl font-semibold">Riepilogo</h3>
            <div className="space-y-1 pb-2 border-b border-border">
              {(avail?.lines || []).map((l) => (
                <div key={l.service_id} className="flex justify-between text-sm" data-testid={`book-line-${l.service_id}`}>
                  <span>{l.quantity > 1 ? `${l.quantity}× ` : ""}{l.name}{l.reason === "included" ? " (incluso)" : ""}</span>
                  <span className="font-medium">{l.reason === "included" ? "—" : eur(l.line_total)}</span>
                </div>
              ))}
              {!avail?.lines && <Row l="Servizio" v={service.name} />}
            </div>
            <Row l="Data" v={new Date(slot.start).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })} />
            <Row l="Ora" v={slot.time} />
            <Row l="Professionista" v={slot.operators[0].name} />
            <Row l="Durata stimata" v={`${avail.duration_min} min`} />
            <Row l="Prezzo previsto" v={`${avail.price_from ? "da " : ""}${eur(avail.price)}`} />
          </Card>
          <p className="text-xs text-muted-foreground">Cancellazione gratuita fino a {cfg?.cancellation_window_hours || 24}h prima. {avail.price_from && "Il prezzo definitivo può variare in base alla consulenza."}</p>

          {/* Consensi & Privacy (GDPR) */}
          <Card className="p-4 space-y-4" data-testid="book-consent-card">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
              <h3 className="font-heading text-base font-semibold">Consensi e privacy</h3>
            </div>

            <p className="text-xs leading-relaxed text-muted-foreground" data-testid="book-consent-operational-text">
              I recapiti forniti saranno utilizzati da <span className="font-medium text-foreground">Hair Identity di Filice Giuseppe</span> esclusivamente per gestire la prenotazione e inviare, tramite email o WhatsApp, conferme, promemoria, eventuali modifiche o cancellazioni e altre comunicazioni strettamente necessarie relative all'appuntamento. Questi messaggi non hanno finalità promozionale.
            </p>

            <label className="flex items-start gap-3 cursor-pointer" htmlFor="book-privacy-read">
              <Checkbox id="book-privacy-read" checked={privacyRead} onCheckedChange={(v) => setPrivacyRead(!!v)} className="mt-0.5" data-testid="book-privacy-checkbox" />
              <span className="text-sm leading-relaxed">
                Dichiaro di aver letto e compreso l'
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-[hsl(var(--hi-gold))] underline underline-offset-2 hover:text-[hsl(var(--hi-gold-hover))]" data-testid="book-privacy-link">informativa privacy</a>.
                <span className="text-[hsl(var(--hi-status-danger))]"> *</span>
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer" htmlFor="book-marketing">
              <Checkbox id="book-marketing" checked={marketingConsent} onCheckedChange={(v) => setMarketingConsent(!!v)} className="mt-0.5" data-testid="book-marketing-checkbox" />
              <span className="text-sm leading-relaxed text-muted-foreground">
                Desidero ricevere da Hair Identity, tramite email e WhatsApp, promemoria personalizzati per il mantenimento dei servizi effettuati, richieste di recensione, novità, offerte e comunicazioni promozionali. Potrò revocare il consenso in qualsiasi momento.
              </span>
            </label>
          </Card>

          <Button className="w-full" onClick={confirm} disabled={loading || !privacyRead} data-testid="book-confirm">{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma prenotazione</Button>
          {!privacyRead && <p className="text-xs text-center text-muted-foreground -mt-2" data-testid="book-privacy-hint">Per proseguire è necessario confermare la presa visione dell'informativa privacy.</p>}
        </div>
      )}

      {/* Lista d'attesa: finestra informativa prima dell'inserimento */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent data-testid="waitlist-info-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Lista d'attesa</DialogTitle></DialogHeader>
          <p className="text-sm leading-relaxed text-muted-foreground" data-testid="waitlist-info-text">
            La lista d'attesa non costituisce un appuntamento confermato. La tua richiesta verrà inviata al salone e, se il professionista riuscirà a trovare uno spazio per il servizio e il giorno richiesti, riceverai un messaggio con l'orario dell'appuntamento. Considera la prenotazione confermata soltanto dopo aver ricevuto il messaggio di approvazione.
          </p>
          <div className="space-y-3 pt-1">
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><div className="font-medium text-sm">Disponibile a qualsiasi orario</div><div className="text-xs text-muted-foreground">Aumenta le possibilità di trovare posto</div></div>
              <Switch checked={wlAnyTime} onCheckedChange={setWlAnyTime} data-testid="waitlist-anytime" />
            </div>
            {!wlAnyTime && (
              <div className="space-y-1.5"><Label>Orario preferito (facoltativo)</Label>
                <Input type="time" value={wlPreferredTime} onChange={(e) => setWlPreferredTime(e.target.value)} data-testid="waitlist-preferred-time" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaitlistOpen(false)} data-testid="waitlist-cancel">Annulla</Button>
            <Button onClick={joinWaitlist} disabled={wlBusy} data-testid="waitlist-confirm">{wlBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma lista d'attesa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function joinWaitlist() {
    setWlBusy(true);
    const op = operator !== "any" ? operator : null;
    const opName = op ? [...new Map((avail?.slots || []).flatMap((s) => s.operators).map((o) => [o.id, o])).values()].find((o) => o.id === op)?.name : null;
    api.post("/portal/waitlist", {
      service_ids: [service.id], service_names: [service.name], days: [date],
      preferred_time: wlAnyTime ? null : (wlPreferredTime || null),
      any_time: wlAnyTime, time_ranges: [],
      operator_id: op, operator_name: opName,
    })
      .then(() => { setWaitlistOpen(false); toast.success("Richiesta inviata: sei in lista d'attesa"); })
      .catch(() => toast.error("Errore invio richiesta"))
      .finally(() => setWlBusy(false));
  }
}

function optionalDepsOf(s, byId) {
  return (s.dependencies || []).filter((d) => d.type === "optional").map((d) => byId[d.service_id]).filter(Boolean);
}
const Row = ({ l, v }) => <div className="flex justify-between text-sm"><span className="text-muted-foreground">{l}</span><span className="font-medium text-right">{v}</span></div>;
